import {
    FC,
    forwardRef,
    PropsWithChildren, RefAttributes,
    useCallback,
    useImperativeHandle,
    useState
} from "react";
import {styled} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from '@mui/material/IconButton';
import Drawer from "@mui/material/Drawer";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import MuiListItemText from '@mui/material/ListItemText';
import Box from "@mui/material/Box";
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PasswordIcon from "@mui/icons-material/Password";
import {useAuth} from "../lib/auth.ts";
import {useRouter} from "../hooks/useRouter";
import {Divider} from "@memaro/components/Menu.tsx";

const StyledAppBar = styled(AppBar)({
    background: "transparent",
    boxShadow: "none",
})

const StyledDrawer = styled(Drawer)({
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: 320,
        boxSizing: 'border-box',
    }
})

export const ListItemText = styled(MuiListItemText)({
    '& .MuiTypography-root': {
        font: "inherit"
    }
})

const DrawerHeader = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

export type MenuHandle = {
    close: () => void
}

export type MenuProps = PropsWithChildren & RefAttributes<MenuHandle> & {
    onSignOut?: () => void
}

const Menu: FC<MenuProps> = forwardRef<MenuHandle, MenuProps>(({onSignOut, children}, ref) => {
    const {gotoLogin, gotoChangePassword} = useRouter();
    const {state, signOut} = useAuth();
    const [open, setOpen] = useState(false);

    const handleDrawerOpen = useCallback(() => setOpen(true), [])
    const handleDrawerClose = useCallback(() => setOpen(false), [])
    const handleSignOut = useCallback(async () => {
        handleDrawerClose();
        await signOut({
            global: false,
            oauth: {
                redirectUrl: `${window.location.origin}/login`
            }
        })
        onSignOut?.()
    }, [handleDrawerClose, signOut, onSignOut])

    useImperativeHandle(ref, () => ({
        close: handleDrawerClose
    }), [handleDrawerClose]);

    return (
        <>
            <StyledAppBar position="fixed">
                <Toolbar>
                    <IconButton color="primary" onClick={handleDrawerOpen}>
                        <MenuIcon/>
                    </IconButton>
                </Toolbar>
            </StyledAppBar>
            <StyledDrawer anchor="left" open={open} onClose={handleDrawerClose} ModalProps={{keepMounted: true}}>
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        <ChevronLeftIcon/>
                    </IconButton>
                </DrawerHeader>
                {children}
                {children ? (
                    <Divider>Uw account</Divider>
                ) : null}
                <List>
                    {state == "authenticated" ? (
                        <>
                            <ListItem disablePadding>
                                <ListItemButton onClick={gotoChangePassword}>
                                    <ListItemIcon>
                                        <PasswordIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Wachtwoord wijzigen"/>
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton onClick={handleSignOut}>
                                    <ListItemIcon>
                                        <LogoutIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary="Uitloggen"/>
                                </ListItemButton>
                            </ListItem>
                        </>
                    ) : (
                        <ListItem disablePadding>
                            <ListItemButton onClick={gotoLogin}>
                                <ListItemIcon>
                                    <LoginIcon/>
                                </ListItemIcon>
                                <ListItemText primary="Inloggen"/>
                            </ListItemButton>
                        </ListItem>
                    )}
                </List>
            </StyledDrawer>
        </>
    );
})

export default Menu;
